import { create } from 'zustand';
/**
 * Factory for a per-instance Zustand tree store.
 *
 * @param initialExpanded - Optional pre-computed set of node IDs to expand on mount
 *                         (typically built via `computeInitialExpanded`).
 */
export function createTreeStore(initialExpanded) {
    return create((set, get) => ({
        expandedItems: new Set(initialExpanded !== null && initialExpanded !== void 0 ? initialExpanded : []),
        toggleExpand: (id) => set((state) => {
            const next = new Set(state.expandedItems);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                next.add(id);
            }
            return { expandedItems: next };
        }),
        setExpandedItems: (ids) => set({ expandedItems: new Set(ids) }),
        isExpanded: (id) => get().expandedItems.has(id),
        draggedItemId: null,
        setDraggedItemId: (id) => set({ draggedItemId: id }),
        hoveredNodeId: null,
        dropPosition: null,
        canDrop: false,
        setDragHover: (nodeId, position, canDrop) => set({ hoveredNodeId: nodeId, dropPosition: position, canDrop }),
        clearDragHover: (nodeId) => set((state) => {
            // If a specific node was provided, only clear when it matches — this
            // guards against stale leave events overwriting a newer enter event
            // on a sibling node during rapid drag movement.
            if (nodeId !== undefined && state.hoveredNodeId !== nodeId) {
                return state;
            }
            return { hoveredNodeId: null, dropPosition: null, canDrop: false };
        }),
        reset: () => set({
            draggedItemId: null,
            hoveredNodeId: null,
            dropPosition: null,
            canDrop: false
        })
    }));
}
// ---------------------------------------------------------------------------
// Selector factories — enable per-node selective subscriptions so only the
// affected node re-renders when its slice of state changes.
// ---------------------------------------------------------------------------
export const selectIsExpanded = (id) => (state) => state.expandedItems.has(id);
export const selectIsDragging = (id) => (state) => state.draggedItemId === id;
export const selectIsDraggedOver = (id) => (state) => state.hoveredNodeId === id;
export const selectDropPosition = (id) => (state) => state.hoveredNodeId === id ? state.dropPosition : null;
export const selectCanDrop = (id) => (state) => state.hoveredNodeId === id ? state.canDrop : false;
/**
 * Compute the set of node IDs whose depth is strictly less than
 * `expandedByDefault` (i.e. their children should be visible on initial mount).
 *
 * Depth is computed from the flat `parentId` relationships in a single pass
 * using memoization — O(n) worst case.
 *
 * If `expandedByDefault` is `0` or negative, returns an empty set.
 */
export function computeInitialExpanded(items, expandedByDefault) {
    const expanded = new Set();
    if (!Number.isFinite(expandedByDefault) || expandedByDefault <= 0 || items.length === 0) {
        return expanded;
    }
    const byId = new Map();
    items.forEach((item) => {
        byId.set(item.id, item);
    });
    const depthCache = new Map();
    const depthOf = (id) => {
        const cached = depthCache.get(id);
        if (cached !== undefined)
            return cached;
        // Iterative walk up parent chain, guarding against cycles.
        const visited = new Set();
        let cursor = id;
        let depth = 0;
        while (cursor !== null && cursor !== undefined) {
            if (visited.has(cursor))
                break;
            visited.add(cursor);
            const node = byId.get(cursor);
            const parent = node === null || node === void 0 ? void 0 : node.parentId;
            if (parent === null || parent === undefined)
                break;
            depth += 1;
            cursor = parent;
        }
        depthCache.set(id, depth);
        return depth;
    };
    items.forEach((item) => {
        if (depthOf(item.id) < expandedByDefault) {
            expanded.add(item.id);
        }
    });
    return expanded;
}
